import type {User} from "account/User";
import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import {planOf} from "home/gating";
import type {SubscriptionCheckoutTerms} from "pricing/PricingApi";
import type {BillingPeriod, PaidPlanId} from "pricing/planCatalogue";
import {resumeFreeCheckout, type FreeCheckoutPorts} from "offers/resumeFreeCheckout";
import type {PromoGift} from "./PromoOfferApi";

/** An incoming capability, not attribution. Duplicates must not pick an arbitrary offer. */
export function parsePromoCode(search: string): string | null {
	const codes = new URLSearchParams(search).getAll("promo");
	if (codes.length !== 1) return null;
	// Trimmed: surrounding whitespace is a copy-paste artifact, and the api would refuse it verbatim.
	const code = codes[0].trim();
	return code.length > 0 && code.length <= 256 ? code : null;
}

/**
 * Display names for the platforms our share links name. An ALLOWLIST on purpose: the value comes
 * from the URL, which anyone can write, and an unrecognized token must never become dialog copy
 * ("Special free-money coupon"). `utm_source` names the platform directly and wins as the more
 * specific claim; otherwise the FIRST HYPHEN-SEPARATED SEGMENT of `utm_campaign` is tried — the
 * organic-link convention writes "<platform>-posts", and a bare "reddit" matches too.
 */
const PROMO_PLATFORMS: Record<string, string> = {
	reddit: "Reddit", threads: "Threads", facebook: "Facebook", instagram: "Instagram",
	x: "X", twitter: "X", tiktok: "TikTok", linkedin: "LinkedIn", youtube: "YouTube", bluesky: "Bluesky",
};

/** Own-property check, never a bare index: "constructor" in the URL must not name Object.prototype members. */
function platformOf(token: string | undefined): string | null {
	return token !== undefined && Object.prototype.hasOwnProperty.call(PROMO_PLATFORMS, token)
		? PROMO_PLATFORMS[token] : null;
}

export function promoPlatformName(search: string): string | null {
	const params = new URLSearchParams(search);
	const source = platformOf(params.get("utm_source")?.toLowerCase());
	const campaign = platformOf(params.get("utm_campaign")?.toLowerCase().split("-")[0]);
	return source ?? campaign;
}

/** Wait for auth to settle so a returning paid subscriber never sees a guest offer flash. */
export function promoAudience(user: User, session: AuthenticatedSessionState): boolean {
	return session !== AuthenticatedSessionState.Pending && planOf(user) === "free";
}

/**
 * Whether the resolved offer is on screen. Dismissal is a THIS-VISIT fact held in memory only — never
 * stored — so a reload of the still-valid link shows the dialog again. The plan check keeps it from
 * surviving the moment an account is visibly paid (a mid-dialog sign-in, or the payment itself);
 * session settling gates only the LOOKUP, so a transient re-hydration cannot blink the open dialog.
 */
export function promoDialogOpen(user: User, offer: PromoGift | null, dismissed: boolean): boolean {
	return offer !== null && !dismissed && planOf(user) === "free";
}

/** Monthly Plus with the code, through the shared sign-in continuation ({@link resumeFreeCheckout}). */
export async function resumePromoCheckout(code: string, ports: Omit<FreeCheckoutPorts, "start"> & {
	start: (plan: PaidPlanId, period: BillingPeriod, terms: SubscriptionCheckoutTerms) => Promise<void>;
}): Promise<void> {
	await resumeFreeCheckout({...ports, start: () => ports.start("plus", "MONTHLY", {promoCode: code})});
}
